import {
  ExclamationCircleOutlined,
  MoreOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import {
  Col,
  Dropdown,
  Modal,
  Row,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { MenuInfo } from 'rc-menu/lib/interface';
import React, { useCallback, useState } from 'react';

import { customerApi } from '../../api/customerApi';
import {
  BILLING_STATUS_COLORS,
  BILLING_STATUS_LABEL,
  EPaymentMethod,
  PAYMENT_METHOD_LABEL,
} from '../../constants/financial';
import { EBillingStatus } from '../../constants/financial';
import {
  useBillings,
  useCancelAutoRescue,
  useReasonOptions,
} from '../../hooks/useCustomerFinancial';
import { Billing } from '../../types/customer';
import { customerRefundPdf } from '../../utils/customerAdyenReportUtils';
import { dateUtils } from '../../utils/dates';
import { stringUtils } from '../../utils/strings';
import { ToastMessage } from '../ToastMessage';
import { CustomRow } from './components/CustomRow';
import { SelectReason } from './components/SelectReason';
import { RefundModal } from './RefundModal';
import TransactionsTable from './TransactionsTable';

export type BillingsTableProps = {
  subscriptionId: number;
  customerDocument: string;
  customerEmail: string;
};
const { confirm } = Modal;
const { Text } = Typography;
const DATE_FORMAT = 'dd/MM/yyy HH:mm:ss';

export const BillingsTable: React.FC<BillingsTableProps> = ({
  subscriptionId,
  customerDocument,
  customerEmail,
}) => {
  const [selectedBilling, setSelectedBilling] = useState<Billing>();
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [selectedReason, setSelectedReason] = useState<number>(0);
  const { data: billings } = useBillings(subscriptionId);
  const { cancelAutoRescue } = useCancelAutoRescue();

  const handleChange = (value: number) => {
    setSelectedReason(value);
  };

  const { data: options } = useReasonOptions();
  const showCancelAutoRescueModal = useCallback(() => {
    confirm({
      icon: <ExclamationCircleOutlined style={{ color: '#FAAD14' }} />,
      title: <Text style={{ fontWeight: 600 }}>Cancelar Auto Rescue</Text>,
      content: (
        <SelectReason
          description='Tem certeza que deseja cancelar o auto rescue? Essa ação cancelará o motor de recuperação e também a assinatura do cliente. Selecione um motivo para continuar:'
          confirmationAsk='Esta alteração não poderá ser desfeita. Tem certeza que deseja prosseguir com a alteração?'
          handleChange={handleChange}
          options={options}
        />
      ),
      centered: true,
      width: 704,
      okText: 'Cancelar Auto Rescue',
      okType: 'danger',
      okButtonProps: { type: 'primary' },
      cancelText: 'Cancelar',
      onOk: () => {
        cancelAutoRescue({ subscriptionId, reasonId: selectedReason });
        setShowRefundModal(false);
      },
    });
  }, []);

  const modalMapping: Record<string, () => void> = {
    refund: () => setShowRefundModal(true),
    cancel: showCancelAutoRescueModal,
  };

  const handleMenuClick = async (e: MenuInfo, record: Billing) => {
    const action = e.key;
    setSelectedBilling(record);
    const showModal = modalMapping[action];

    if (showModal) showModal();

    if (action === 'report') {
      try {
        const { data: refundPDFData } = await customerApi.getRefundPDF(
          record.lastPspReference,
          customerEmail
        );
        if (refundPDFData) {
          customerRefundPdf(refundPDFData.data);
        }
      } catch {
        ToastMessage({
          content: `Erro ao emitir relatório. Por favor, tente novamente!`,
          type: 'error',
        });
      }
    }
  };

  const generateMenuItems = useCallback(
    (rowData: Billing): MenuProps['items'] => [
      {
        label: 'Reembolso',
        key: `refund`,
        style: {
          color:
            rowData.refundDate || rowData.status === EBillingStatus.paid
              ? 'rgba(22, 104, 220, 1)'
              : undefined,
        },
        disabled:
          !!rowData.refundDate || rowData.status !== EBillingStatus.paid,
      },
      {
        type: 'divider',
      },
      {
        label: 'Emissão de Relatório',
        key: `report`,
        style: {
          color: rowData.refundDate ? 'rgba(22, 104, 220, 1)' : undefined,
        },
        disabled: rowData.status !== EBillingStatus.refunded,
      },
    ],
    []
  );

  const billingColumns: ColumnsType<Billing> = [
    {
      title: 'ID Billing',
      dataIndex: 'id',
      key: 'id',
      render: id => <CustomRow>{id}</CustomRow>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: EBillingStatus) => (
        <CustomRow>
          <Tag color={BILLING_STATUS_COLORS[status]}>
            {BILLING_STATUS_LABEL[status]}
          </Tag>
        </CustomRow>
      ),
    },
    {
      title: 'Valor',
      dataIndex: 'amount',
      key: 'amount',
      render: amount => (
        <CustomRow>{stringUtils.formatToReal(amount)}</CustomRow>
      ),
    },
    {
      title: 'Data de Vencimento',
      dataIndex: 'dueDate',
      key: 'dueDate',
      render: dueDate => (
        <CustomRow>
          {dateUtils.formatDateString(dueDate, DATE_FORMAT)}
        </CustomRow>
      ),
    },
    {
      title: 'Data de Pagamento',
      dataIndex: 'paymentDate',
      key: 'paymentDate',
      render: paymentDate => (
        <CustomRow>
          {dateUtils.formatDateString(paymentDate, DATE_FORMAT)}
        </CustomRow>
      ),
    },
    {
      title: (
        <Row justify={'space-between'} align={'middle'}>
          <Col>
            <Text>PSP da última transação</Text>
          </Col>
          <Col>
            <Tooltip title='Exibe o PSP da ultima transação realizada.'>
              <QuestionCircleOutlined style={{ fontSize: '22px' }} />
            </Tooltip>
          </Col>
        </Row>
      ),
      dataIndex: 'lastPspReference',
      key: 'lastPspReference',
      render: lastPspReference => <CustomRow>{lastPspReference}</CustomRow>,
    },
    {
      title: 'Parcela Atual/Total',
      dataIndex: 'installmentResume',
      key: 'installmentResume',
      render: lastPspReference => <CustomRow>{lastPspReference}</CustomRow>,
    },
    {
      title: 'Data Estorno',
      dataIndex: 'refundDate',
      key: 'refundDate',
      render: refundDate => (
        <CustomRow>
          {dateUtils.formatDateString(refundDate, DATE_FORMAT) ||
            'Não se aplica'}
        </CustomRow>
      ),
    },
    {
      title: 'Forma de pagamento',
      dataIndex: 'paymentMethod',
      key: 'paymentMethod',
      render: (paymentMethod: EPaymentMethod) => (
        <CustomRow>{PAYMENT_METHOD_LABEL[paymentMethod]}</CustomRow>
      ),
    },
    {
      title: 'Ações',
      key: 'actions',
      fixed: 'right',
      render: (_, record) => (
        <Row justify={'center'} align={'middle'} style={{ cursor: 'pointer' }}>
          <Dropdown
            menu={{
              items: generateMenuItems(record),
              onClick: e => handleMenuClick(e, record),
            }}
            trigger={['click']}
          >
            <Col>
              <Space>
                <MoreOutlined />
              </Space>
            </Col>
          </Dropdown>
        </Row>
      ),
    },
  ];
  return (
    <>
      <Table
        dataSource={billings}
        columns={billingColumns}
        rowKey='id'
        pagination={false}
        expandable={{
          expandedRowRender: billing => (
            <TransactionsTable billingId={billing.id} />
          ),
        }}
      />
      {showRefundModal && (
        <RefundModal
          open={showRefundModal}
          onClose={() => setShowRefundModal(false)}
          billing={selectedBilling}
          customerDocument={customerDocument}
        />
      )}
    </>
  );
};
